import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import {
  Image as ImageIcon,
  Upload,
  Trash2,
  Plus,
  Layout,
  Event,
  Store,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  ArrowUp
} from 'lucide-react';

const AdminGallery = () => {
  const { slug } = useParams();
  const [search] = useSearchParams();
  const category = (search.get('type') === 'venue') ? 'venue' : 'event';
  const isOnboardingPhotos = search.get('onboarding') === 'photos' && category === 'venue';

  const { user } = useSelector((state) => state.auth);

  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [message, setMessage] = useState(null); // { type, text }
  const [loading, setLoading] = useState(false);

  // listado
  const [images, setImages] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const [salon, setSalon] = useState(null);
  const [loadingSalon, setLoadingSalon] = useState(true);

  const fetchSalon = async () => {
    try {
      const { data } = await axios.get(`/api/clients/${slug}`);
      setSalon(data);
    } catch (err) {
      console.error('Error fetching salon:', err);
    } finally {
      setLoadingSalon(false);
    }
  };

  const fetchImages = async () => {
    setLoadingList(true);
    try {
      const { data } = await axios.get(`/${slug}/api/images`, {
        params: { category },
        withCredentials: true,
      });
      setImages(Array.isArray(data?.images) ? data.images : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchSalon();
  }, [slug]);

  useEffect(() => {
    fetchImages();
    setSelectedFile(null);
    setPreviewUrl(null);
    setMessage(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [slug, category]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setMessage(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setMessage(null);

    try {
      let gallery;
      try {
        const { data } = await axios.get(`/${slug}/api/images/gallery`, { withCredentials: true });
        gallery = data;
      } catch (err) {
        if (err.response?.status === 404) {
          const { data } = await axios.post(`/${slug}/api/images/gallery`, null, { withCredentials: true });
          gallery = data;
        } else {
          throw err;
        }
      }

      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('category', category);

      await axios.post(`/${slug}/api/images/${gallery._id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });

      setMessage({ type: 'success', text: 'Imagen subida correctamente' });
      setSelectedFile(null);
      setPreviewUrl(null);
      if (fileInputRef.current) fileInputRef.current.value = '';

      fetchImages();
    } catch (error) {
      console.error(error);
      setMessage({ type: 'error', text: error?.response?.data?.message || 'Error al subir la imagen' });
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta imagen?')) return;

    try {
      setDeletingId(imageId);
      await axios.delete(`/${slug}/api/images/${imageId}`, { withCredentials: true });
      setImages((arr) => arr.filter((img) => img._id !== imageId));
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: 'No se pudo eliminar la imagen' });
    } finally {
      setDeletingId(null);
      setTimeout(() => setMessage(null), 3000);
    }
  };



  const isSports = salon?.organizationType === 'sports';

  const venueLabel = isSports ? 'Instalaciones y Predio' : 'Ambiente y Salón';
  const eventLabel = isSports ? 'Torneos Realizados' : 'Eventos Realizados';
  
  const title = category === 'venue' ? venueLabel : eventLabel;
  const icon = category === 'venue' ? <Store size={20} /> : <Layout size={20} />;

  return (
    <div className="max-w-6xl mx-auto py-10 px-4 pb-24 animate-fade-in relative">

      {/* Toast Feedback */}
      {message && (
        <div className={`fixed top-24 right-4 z-[100] px-6 py-3 rounded-2xl border shadow-2xl animate-fade-in-up flex items-center gap-3 backdrop-blur-xl
          ${message.type === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>
          {message.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          <span className="font-bold text-sm tracking-tight">{message.text}</span>
        </div>
      )}

      {/* Header STICKY */}
      <div className="sticky top-16 z-40 -mx-4 px-4 py-6 mb-12 bg-background/80 backdrop-blur-md border-b border-white/5 flex flex-col md:flex-row md:items-center justify-between gap-6 transition-all">
        <div className="animate-fade-in-up">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary-orange/10 rounded-xl text-primary-orange">
              <ImageIcon size={18} />
            </div>
            <span className="text-[10px] font-black uppercase tracking-[3px] text-white/30">Galería Multimedia</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight leading-none">
            {title.split(' ')[0]} <span className="text-gradient">{title.split(' ').slice(1).join(' ')}</span>
          </h1>
        </div>

        {/* Category Switcher */}
        <div className="flex bg-white/5 p-1 rounded-2xl border border-white/5 animate-fade-in-up">
          <Link
            to={`/${slug}/admin/gallery?type=event`}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${category === 'event' ? 'bg-brand-gradient text-white shadow-lg' : 'text-white/40 hover:text-white'
              }`}
          >
            <Layout size={14} />
            {isSports ? 'Torneos' : 'Eventos'}
          </Link>
          <Link
            to={`/${slug}/admin/gallery?type=venue`}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${category === 'venue' ? 'bg-brand-gradient text-white shadow-lg' : 'text-white/40 hover:text-white'
              }`}
          >
            <Store size={14} />
            {isSports ? 'Predio' : 'Salón'}
          </Link>
        </div>
      </div>

      {isOnboardingPhotos && (
        <div className="mb-8 p-5 md:p-6 bg-primary-orange/10 border border-primary-orange/20 rounded-[28px] flex items-start gap-4">
          <div className="w-11 h-11 rounded-2xl bg-primary-orange/15 text-primary-orange flex items-center justify-center shrink-0">
            <CheckCircle2 size={22} />
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-[3px] text-primary-orange/70 mb-1">Paso del onboarding</p>
            <h2 className="text-lg font-black text-white">Subí fotos reales de tu espacio</h2>
            <p className="text-sm text-white/50 font-medium leading-relaxed mt-1">
              Tocá el recuadro para elegir una imagen y después confirmá la subida. Con una foto alcanza para completar este paso.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-12">

        {/* Uploader Section */}
        <section className="bg-surface p-8 md:p-10 rounded-[40px] border border-white/5 shadow-2xl relative overflow-hidden group animate-fade-in-up">
          <div className="absolute top-0 left-0 w-full h-1 bg-brand-gradient opacity-30 group-hover:opacity-100 transition-opacity"></div>

          <div className="flex flex-col md:flex-row gap-10 items-center">

            {/* Foto Picker / Preview */}
            <div className="shrink-0">
              <div
                onClick={() => fileInputRef.current?.click()}
                className="w-48 h-48 md:w-56 md:h-56 rounded-[32px] border-4 border-dashed border-white/10 hover:border-primary-orange/50 bg-white/5 flex flex-col items-center justify-center cursor-pointer overflow-hidden transition-all group relative"
              >
                {previewUrl ? (
                  <>
                    <img src={previewUrl} alt="Preview" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                      <Plus className="text-white" size={32} />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center text-white/20 mb-4 group-hover:bg-primary-orange/10 group-hover:text-primary-orange transition-all">
                      <Plus size={32} />
                    </div>
                    <span className="text-[10px] font-black uppercase text-white/30 tracking-widest text-center px-4">
                      Click para elegir foto
                    </span>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/*"
                onChange={handleFileChange}
              />
            </div>

            {/* Info and Action */}
            <div className="flex-1 w-full space-y-6">
              <div>
                <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                  <Upload size={20} className="text-primary-orange" />
                  Nueva Captura
                </h3>
                <p className="text-white/40 text-sm leading-relaxed max-w-md">
                  Subí fotos de alta calidad para destacar la {category === 'venue' ? (isSports ? 'infraestructura del predio' : 'infraestructura del salón') : (isSports ? 'atmósfera de tus torneos' : 'atmósfera de tus eventos')}. Formatos aceptados: JPG, PNG, WEBP.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <button
                  onClick={handleUpload}
                  disabled={loading || !selectedFile}
                  className="bg-brand-gradient text-white px-10 py-4 rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl shadow-orange-500/20 hover:opacity-90 transition-all active:scale-95 disabled:opacity-50 flex items-center gap-3"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin" size={16} />
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      Subir a la Galería
                    </>
                  )}
                </button>

                {selectedFile && (
                  <button
                    onClick={() => { setSelectedFile(null); setPreviewUrl(null); }}
                    className="text-[10px] font-black uppercase text-red-500/60 hover:text-red-500 tracking-widest transition-colors"
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </div>

          </div>
        </section>

        {/* Gallery List */}
        <section className="animate-fade-in-up [animation-delay:100ms]">
          <div className="flex items-center justify-between mb-8 px-2">
            <div>
              <h3 className="text-xl font-bold text-white tracking-tight">Fotos Publicadas</h3>
              <p className="text-[10px] text-white/20 font-bold uppercase tracking-widest mt-1">
                {loadingList ? 'Cargando biblioteca...' : `${images.length} Archivos encontrados`}
              </p>
            </div>
          </div>

          {loadingList ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="aspect-square rounded-3xl bg-white/5 animate-pulse" />
              ))}
            </div>
          ) : images.length === 0 ? (
            <div className="bg-white/5 border border-dashed border-white/10 rounded-[40px] p-20 text-center">
              <ImageIcon size={48} className="text-white/10 mx-auto mb-4" />
              <p className="text-white/40 font-medium">Todavía no hay fotos en esta categoría.</p>
              <p className="text-[10px] text-white/20 font-bold uppercase tracking-widest mt-2 italic">
                Cargá tu primera imagen arriba 👆
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {images.map((img) => (
                <div key={img._id} className="group relative aspect-square rounded-[32px] overflow-hidden bg-surface border border-white/5 shadow-xl hover:shadow-2xl hover:border-primary-orange/30 transition-all duration-500">
                  <img
                    src={img.url}
                    alt="Galería"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />

                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-end p-4">
                    <button
                      onClick={() => handleDelete(img._id)}
                      disabled={deletingId === img._id}
                      className="w-10 h-10 bg-red-500 text-white rounded-xl flex items-center justify-center hover:bg-red-600 active:scale-90 transition-all shadow-xl"
                      title="Eliminar foto"
                    >
                      {deletingId === img._id ? (
                        <Loader2 className="animate-spin" size={18} />
                      ) : (
                        <Trash2 size={18} />
                      )}
                    </button>
                  </div>

                  {/* Icon indicator */}
                  <div className="absolute top-4 left-4 p-1.5 bg-black/40 backdrop-blur-md rounded-lg text-white/50 border border-white/10 opacity-60">
                    {icon}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

      </div>

      {/* Floating Scroll to Top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 w-12 h-12 bg-surface border border-white/10 rounded-full flex items-center justify-center text-white/40 hover:text-primary-orange hover:border-primary-orange/50 transition-all shadow-2xl z-50 backdrop-blur-md"
      >
        <ArrowUp size={20} />
      </button>

    </div>
  );
};

export default AdminGallery;
